import { Badge } from '@savvy-kit/components/badge';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@savvy-kit/components/hover-card';
import { cn } from '@savvy-kit/lib/utils';
import { isNil } from 'es-toolkit';
import {
  Ban,
  Banknote,
  ChartNoAxesColumnIncreasing,
  CircleCheckBig,
  CircleDashed,
  CircleDot,
  ClipboardCheck,
  UserRound,
  XCircleIcon,
} from 'lucide-react';

import type {
  TaxEnrollmentAprilAppStatus,
  TaxEnrollmentStatus,
  TaxEnrollmentTypeName,
  TaxReturnStatus,
  TaxTranscriptRequestStatus,
} from '@savvy/constants/generated/gql-enums';

type StateFiling = {
  readonly stateCode: string;
  readonly status: TaxReturnStatus;
};

type TaxStatusBadgeProps = {
  readonly enrollmentStatus: TaxEnrollmentStatus;
  readonly enrollmentType?: TaxEnrollmentTypeName | null;
  readonly transcriptStatus?: TaxTranscriptRequestStatus | null;
  readonly federalReturnStatus?: TaxReturnStatus | null;
  readonly aprilAppStatus?: TaxEnrollmentAprilAppStatus | null;
  readonly stateFilings?: StateFiling[] | null;
  readonly taxFilingEnabled?: boolean;
};

type StatusConfig = {
  readonly label: string;
  readonly icon: React.ComponentType<{ className?: string }>;
  readonly variant:
    | 'secondary-muted'
    | 'destructive'
    | 'info-muted'
    | 'warning'
    | 'warning-muted'
    | 'primary'
    | 'primary-muted';
  readonly className?: string;
};

const APRIL_IN_PROGRESS_STATUSES: TaxEnrollmentAprilAppStatus[] = [
  'questionnaire_in_progress',
  'questionnaire_completed',
  'ready_to_submit',
];

export function TaxStatusBadge({
  enrollmentStatus,
  enrollmentType,
  transcriptStatus,
  federalReturnStatus,
  aprilAppStatus,
  stateFilings,
  taxFilingEnabled,
}: TaxStatusBadgeProps) {
  const hasFederalReturnStatus = hasFederalReturn(federalReturnStatus);
  const filingInProgress = !!aprilAppStatus && APRIL_IN_PROGRESS_STATUSES.includes(aprilAppStatus);
  const hasStateStatuses = (stateFilings?.filter((filing) => filing.status !== 'none') ?? []).length > 0;

  // Return status badges take priority over enrollment status badges.
  if (hasFederalReturnStatus || filingInProgress || hasStateStatuses) {
    return (
      <ReturnStatusBadges
        federalReturnStatus={federalReturnStatus}
        filingInProgress={filingInProgress}
        stateFilings={stateFilings}
      />
    );
  }

  const badgeConfig = resolveEnrollmentStatusConfig({
    enrollmentStatus,
    enrollmentType,
    transcriptStatus,
    taxFilingEnabled,
  });

  const Icon = badgeConfig.icon;
  return (
    <Badge variant={badgeConfig.variant} className={cn('gap-1.5', badgeConfig.className)}>
      <Icon className="size-3" />
      <span>{badgeConfig.label}</span>
    </Badge>
  );
}

// --- Enrollment status config ---

function resolveEnrollmentStatusConfig({
  enrollmentStatus,
  enrollmentType,
  transcriptStatus,
  taxFilingEnabled,
}: TaxStatusBadgeProps): StatusConfig {
  const isTranscriptsOnly = enrollmentType === 'transcript_services_only';
  const isSavvyTaxEnrollment = enrollmentType === 'savvy_single' || enrollmentType === 'savvy_joint';
  const enabledTaxFiling = Boolean(taxFilingEnabled);

  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (enrollmentStatus) {
    case 'not_started':
      return { label: 'Not started', icon: CircleDashed, variant: 'destructive' };

    case 'not_interested':
      return { label: 'Not interested', icon: Ban, variant: 'secondary-muted' };

    case 'registered':
      if (isTranscriptsOnly && transcriptStatus === 'completed')
        return { label: 'Transcript ready', icon: ChartNoAxesColumnIncreasing, variant: 'info-muted' };
      else if (isTranscriptsOnly && transcriptStatus === 'requested')
        return { label: 'Transcript requested', icon: ChartNoAxesColumnIncreasing, variant: 'info-muted' };
      else if (isTranscriptsOnly && transcriptStatus === null)
        return { label: 'Waiting for client', icon: UserRound, variant: 'info-muted' };
      else if (isSavvyTaxEnrollment && enabledTaxFiling) {
        return { label: 'Waiting for client', icon: UserRound, variant: 'info-muted' };
      }
      return { label: 'Registered for filing', icon: ClipboardCheck, variant: 'info-muted' };

    default:
      return { label: 'Not started', icon: CircleDashed, variant: 'destructive' };
  }
}

// --- Return status components + config ---

function hasFederalReturn(status?: TaxReturnStatus | null): status is TaxReturnStatus {
  return !isNil(status) && status !== 'none' && status !== 'not_filed';
}

function resolveReturnStatusConfig(status: TaxReturnStatus): StatusConfig {
  switch (status) {
    case 'accepted':
      return { label: 'accepted', icon: Banknote, variant: 'primary-muted' };
    case 'filed_pending_response':
      return { label: 'submitted', icon: CircleCheckBig, variant: 'warning-muted' };
    case 'rejected':
      return { label: 'rejected', icon: XCircleIcon, variant: 'destructive' };
    case 'not_filed':
      return { label: 'in progress', icon: CircleDot, variant: 'warning-muted' };
    case 'none':
      return { label: '', icon: CircleDashed, variant: 'secondary-muted' };
  }
}

type ReturnStatusBadgesProps = {
  readonly federalReturnStatus?: TaxReturnStatus | null;
  readonly filingInProgress: boolean;
  readonly stateFilings?: StateFiling[] | null;
};

function ReturnStatusBadges({ federalReturnStatus, filingInProgress, stateFilings }: ReturnStatusBadgesProps) {
  const hasFederalReturnStatus = hasFederalReturn(federalReturnStatus);
  const stateReturnStatuses = stateFilings?.filter((filing) => filing.status !== 'none') ?? [];

  return (
    <div className="flex items-center gap-2">
      {hasFederalReturnStatus &&
        (() => {
          const { label, icon: Icon, variant } = resolveReturnStatusConfig(federalReturnStatus);
          return (
            <Badge variant={variant} className="gap-1.5">
              <Icon className="size-3" />
              <span>Federal {label}</span>
            </Badge>
          );
        })()}
      {!hasFederalReturnStatus && filingInProgress && (
        <Badge variant="warning-muted" className="gap-1.5">
          <CircleDot className="size-3" />
          <span>Federal in progress</span>
        </Badge>
      )}
      <StateReturnStatusBadges stateFilings={stateReturnStatuses} />
    </div>
  );
}

function StateReturnStatusBadges({ stateFilings }: { readonly stateFilings: StateFiling[] }) {
  if (stateFilings.length === 0) return null;

  // Single state: use container queries to show/hide the full badge vs. collapsed hover card.
  if (stateFilings.length === 1) {
    return (
      <>
        <div className="@[300px]:hidden">
          <CollapsedStateHoverCard stateFilings={stateFilings} />
        </div>
        <div className="hidden @[300px]:flex gap-2">
          <StateStatusBadgeList stateFilings={stateFilings} />
        </div>
      </>
    );
  }

  // Multiple states: always collapse into a hover card.
  return <CollapsedStateHoverCard stateFilings={stateFilings} />;
}

function CollapsedStateHoverCard({ stateFilings }: { readonly stateFilings: StateFiling[] }) {
  return (
    <HoverCard>
      <HoverCardTrigger
        render={<Badge variant="secondary-muted" className="cursor-pointer gap-1.5 bg-transparent hover:bg-gray-200" />}
      >
        +{stateFilings.length}
      </HoverCardTrigger>
      <HoverCardContent className="w-auto p-2" align="start">
        <div className="flex flex-col gap-2">
          <StateStatusBadgeList stateFilings={stateFilings} />
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

function StateStatusBadgeList({ stateFilings }: { readonly stateFilings: StateFiling[] }) {
  return (
    <>
      {stateFilings.map((filing) => {
        const { label, icon: Icon, variant } = resolveReturnStatusConfig(filing.status);
        return (
          <Badge key={`${filing.stateCode}-${filing.status}`} variant={variant} className="gap-1.5">
            <Icon className="size-3" />
            <span>
              {filing.stateCode} {label}
            </span>
          </Badge>
        );
      })}
    </>
  );
}
